// import * as Comlink from 'comlink'
import type { CarReader } from '@ipld/car/indexed-reader'
import type { Applog, ApplogEnc, CidString } from '@wovin/core/applog'
import type { AppAgent, CoPubKeyRecord, IShare } from '@wovin/core/pubsub'
import type { Thread, WriteableThread } from '@wovin/core/thread'
import type { PromiseType } from '@wovin/core/types'
import type { AgentStateClass } from '../data/agent/AgentState'
import { getGatewayUrl } from '@wovin/connect-gateway'
import {
	dateNowIso,
	getApplogNoCidTypeErrors,
	getApplogTypeErrors,
	isEncryptedApplog,
	isValidApplog,
	removeDuplicateAppLogs,
} from '@wovin/core/applog'
import { cidToString, decodePubFromCar, encodeApplogAndGetCid, toIpnsString, tryParseCID } from '@wovin/core/ipfs'
import { integratePub } from '@wovin/core/pubsub'
import { agentsOfThread, entityOverlapCount, threadFromMaybeArray } from '@wovin/core/query'
import { asReadOnly, ThreadInMemory } from '@wovin/core/thread'
import { sleep, unwrapIfSingle, urlFromHumanInput } from '@wovin/utils'
import { Logger } from 'besonders-logger'
import * as Comlink from 'comlink'
import partition from 'lodash-es/partition'
import { CID } from 'multiformats/cid'
import stringify from 'safe-stable-stringify'
import * as W3Name from 'w3name'
import { decryptWithAesSharedKey, getAESkeyForEncryptedApplogs, getDecryptedSecretFromIDB } from '../data/agent/AgentCrypto'
import { getAgentString, useAgent } from '../data/agent/AgentState'
import { isCoPublisher, recoverCoPublishKey } from '../data/agent/co-publish'
import { insertCompleteApplogsInAppDBifMissing } from '../data/ApplogDB'
import { isFileSystemAPIavailable, verifyPermission, writeFile } from '../data/filesystem-utils'
import { getProviderIDs } from '../data/storage-hooks.ts'
import { getVM } from '../data/VMs/MappedVMbase'
import { ProviderVM } from '../data/VMs/ProviderVM'
import { notifyToast } from '../ui/utils-ui'
import { probeSettleMode, pushCoShareCAS, pushCoShareRitual } from './co-publish-settle'
import { retrievePubToThread } from './retrievePubToThread'
import { storageState } from './storage'
import { reportTransferProgress } from './transfer-progress'
import { getSyncWorker } from './worker-utils'
import { normalizeCID, parseStoredCID } from './cid-utils'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO)

// im  port * as Wasm from 'note3-rs'
// let_wasm: any
// async function initWasm() {
// 	if (!_wasm) {
// 		_wasm = await Wasm.default()
// 		DEBUG('WASM', _wasm, Wasm)
// 		await Wasm.init_rs()
// 	}
// }

export async function pushShare(thread: Thread, share: IShare) {
	DEBUG('[publish]', share, { thread, storageState })
	const agent = useAgent()
	if (!storageState.ucanUploads?.length) throw ERROR(`No upload provider configured (Settings > Storage)`)

	// Ensure agent atoms (agent/appAgent, agent/ecdh, agent/jwkd logs) exist before push
	await agent.ensureAgentAtoms()

	let shareNameString: string

	DEBUG('Publishing datalog snapshot via share:', { ipns: share.id, share })
	shareNameString = share.id

	const SyncWorker = getSyncWorker()
	DEBUG('SyncWorker', SyncWorker)
	const { newLogs, prevCounter } = await SyncWorker.getNewLogsForShareInWorker(share.id, agent.ag)

	if (!newLogs.length) {
		return
	}
	DEBUG('newLogs from Worker', newLogs)
	const prevSnapCID: CID = (share.lastCID) ? parseStoredCID(share.lastCID, 'pushShare prevSnap') : null
	const { cid: rawCid, blob, blocks } = await prepareForPushInWorker(agent, newLogs, share, prevSnapCID, prevCounter)
	// Normalize CIDs that may have lost their prototype through Comlink transfer
	const cid = normalizeCID(rawCid)

	// All push I/O runs in worker (blocks → IDB, remotes, kubo, IPNS)
	const carBytes = new Uint8Array(await blob.arrayBuffer())
	reportTransferProgress(share.id, { kind: 'push', source: 'car-mirror', phase: 'resolving', label: share.name })
	const result = await SyncWorker.pushIOInWorker(
		{
			blocks: blocks.map(b => ({ cid: normalizeCID(b.cid), bytes: b.bytes })),
			cid,
			// Last root the server is known to have — lets car-mirror push skip the whole
			// already-pushed snapshot chain in round 1 (see CarMirrorOptions.prevBoundary)
			prevCID: prevSnapCID ?? undefined,
			carBytes: Comlink.transfer(carBytes, [carBytes.buffer]),
			ipnsPrivateKey: share.pk,
		},
		Comlink.proxy((event) => {
			DEBUG('[pushIO progress]', event)
			if (event.carmirror) {
				const p = event.carmirror
				reportTransferProgress(share.id, {
					kind: 'push',
					source: 'car-mirror',
					phase: 'transferring',
					step: 'remotes',
					round: p.round,
					maxRounds: p.maxRounds,
					blocks: p.blocks,
					blocksThisRound: p.blocksThisRound,
					bytes: p.bytes,
					bytesThisRound: p.bytesThisRound,
					frontier: p.frontier,
					bloomBytes: p.bloomBytes,
					bloomK: p.bloomK,
					requests: p.requests,
				})
			} else {
				reportTransferProgress(share.id, {
					kind: 'push',
					source: 'car-mirror',
					phase: event.status === 'error' ? 'error' : 'transferring',
					step: event.op,
					error: event.error,
				})
			}
		}),
	)

	if (!result.success) {
		reportTransferProgress(share.id, { kind: 'push', phase: 'error', error: 'push I/O failed' })
		const errors = result.events.filter(e => e.status === 'error')
		throw ERROR(`[pushShare] push I/O failed`, errors)
	}
	reportTransferProgress(share.id, { kind: 'push', phase: 'done' })

	await pushToFileSystem(agent, share, cid, blob)

	const pushedCid = normalizeCID(cid).toString()
	const pushedTs = dateNowIso()
	agent.updateShare(share.id, { lastPush: pushedTs, lastCID: pushedCid })
	queueMicrotask(() => void pullCarToEdge(cid, thread))

	// Co-publishing conflict resolution. Only co-shares (`share.coPublish ===
	// true`) enter the settle flow; normal shares keep this fast path. The
	// actual IPNS publish (with `publishIPNSRecord`'s sequence CAS + self-heal)
	// already happened inside the worker's `pushIOInWorker` above. Here we branch
	// to the settle bookkeeping: CAS (zero-wait, just records `won` + the landed
	// seq) or ritual (schedules a 1-minute settle check). On the first push, probe
	// which mode is available (`resolveSequence`-capable target → CAS).
	if (share.coPublish) {
		const settleMode = share.settleMode && share.settleMode !== 'probing' ? share.settleMode : probeSettleMode()
		if (settleMode === 'cas') {
			// Zero-wait: record `won` + the seq the record landed at. Fire-and-forget
			// so the caller isn't blocked by the sequence resolve round-trip.
			void pushCoShareCAS({ ...share, settleMode: 'cas' }, pushedCid).catch(err =>
				ERROR('[pushShare] CAS settle failed', { shareId: share.id, err })
			)
		} else {
			// Ritual: schedule the settle check after the settle window. Non-blocking.
			pushCoShareRitual({ ...share, settleMode: 'ritual' }, thread as WriteableThread, pushedCid, pushedTs)
		}
	}

	return shareNameString
}

async function pushToFileSystem(agent: AgentStateClass, share: IShare, cid, blob: Blob) {
	try {
		if (isFileSystemAPIavailable()) {
			const dirHandle = await agent.getDirectoryHandle() as FileSystemDirectoryHandle
			if (dirHandle) {
				const isReadWrite = await verifyPermission(dirHandle)
				DEBUG({ dirHandle, isReadWrite })
				if (isReadWrite) {
					const ipnsSubfolder = await dirHandle.getDirectoryHandle(share.id, { create: true })
					const carFileHandle = await ipnsSubfolder.getFileHandle(`${cid.toString()}.car`, { create: true })
					DEBUG({ ipnsSubfolder, carFileHandle })
					writeFile(carFileHandle, blob)
				}
			}
		}
	} catch (err) {
		ERROR(`[publish] filesystem write error`, err)
		notifyToast((err as any).message ?? stringify(err, undefined, 2), 'danger')
	}
}

async function prepareForPushInWorker(
	agent: AgentStateClass, /* AppAgent */
	// appThread: Thread,
	logsToPublish: readonly Applog[],
	share: IShare,
	prevSnapCID: CID | null,
	prevCounter: number | null,
) {
	const signerSecret = await getDecryptedSecretFromIDB(agent)

	const appAgentToPass = {
		ag: agent.ag,
		did: agent.did,
		signerSecret: Comlink.transfer(signerSecret, [signerSecret.buffer]),
		/* sign: Comlink.proxy(await agent.getProxyfiableSignFx()) */
	} as unknown as AppAgent
	DEBUG('calling prepareSnapshotForPushInsideWorker with:', { appAgentToPass, logsToPublish, share, prevSnapCID, prevCounter })
	const SyncWorker = getSyncWorker()
	const preparedByWorker = await SyncWorker.prepareSnapshotForPushInsideWorker(
		appAgentToPass,
		// Comlink.proxy(agent),
		logsToPublish,
		JSON.parse(stringify(share)),
		prevSnapCID,
		prevCounter,
	)
	DEBUG({ preparedByWorker })
	return preparedByWorker
}

// export const pullCarToEdge = debounce( // probably should debounce and or reconsider auto push when no changes
export async function pullCarToEdge(cid: CID, thread: Thread) {
	await sleep(1500)
	// const p = performance.now()
	// await retrieveCar(cid)
	// DEBUG('pullCar took:', performance.now() - p, 'ms')
	// const cidStr = cid.toString()
	// // const dwebURL = `https://${cid}.ipfs.dweb.link/?format=car`
	// // const nftsURL = `https://${cid}.ipfs.nftstorage.link/?format=car`
	// const ipfsURL = `https://ipfs.io/ipfs/${cidStr}/?format=car`
	// const cfURL = `https://cloudflare-ipfs.com/ipfs/${cidStr}/?format=car`
	// const evaURL = `https://ipfs.4everland.io/ipfs/${cidStr}/?format=car`
	// const URLs = [/* dwebURL,  nftsURL, */ cfURL, ipfsURL, evaURL]
	const providers = getProviderIDs(undefined, thread)
		.map(id => getVM(ProviderVM, id, thread))
		.filter(provider => provider.type === 'ipfs-gateway')
	for (const provider of providers) {
		// await sleep(150)
		queueMicrotask(async () => {
			const gatewayUrl = getGatewayUrl({ url: urlFromHumanInput(provider.url) }, cid)
			// await sleep(250)
			void fetch(gatewayUrl)
		})
	}

	/**
	 *  some edge URL info;
	 * https://w3s.link/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car
	 * https://dweb.link/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car // redirects to =>
	 * https://baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a.ipfs.dweb.link/?format=car

	 * https://ipfs.io/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car

	 * https://nftstorage.link/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car // redirects to =>
	 * https://baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a.ipfs.nftstorage.link/?format=car

	 * not found:
	 * https://ipfs.runfission.com/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car
	 * no car support:
	 * https://cloudflare-ipfs.com/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car

	 * https://ipfs.4everland.io/ipfs/baguqeerajc3qwxrddm7tdsmnrh4grktyv4s2oljhidxqtqoqm7d33neqbj7a/?format=car
	 */
}

export const updateSubFromPullData = async function(
	pullData: PromiseType<ReturnType<typeof retrievePubToThread>>, // ? ts shenanigans extras, pubToThread, pubAsThread etc...
	subToUpdate: string,
) {
	if (!pullData.didUpdate) return
	const agent = useAgent()

	await agent.updateSub(subToUpdate, {
		lastPull: pullData.ts.toISOString(),
		// lastPullAttempt: should happen earlier
		lastCID: pullData.cid.toString(),
		lastApplogCID: pullData.applogsCID.toString(),
	})
}

export async function tryIntegrateApplogs(
	applogs: Array<Applog | ApplogEnc>,
	infoLogs,
	// ag: string,
	thread: Thread,
	subID: string,
	cid: CID,
) {
	const [encryptedLogs, nonEncryptedLogs] = partition(applogs, isEncryptedApplog) as [ApplogEnc[], Applog[]]
	if (DEBUG.isEnabled) DEBUG('Encrypted?:', { encryptedLogs, nonEncryptedLogs })
	const agent = useAgent()

	const typeboxErrors = (nonEncryptedLogs.map(log => [log, getApplogNoCidTypeErrors(log)]) as [any, any][]).filter(([, errs]) =>
		errs.length
	)
	if (typeboxErrors.length) {
		ERROR(`[tryIntegrateApplogs] typebox check failed:`, typeboxErrors)
		throw new Error(
			`${typeboxErrors.length}/${nonEncryptedLogs.length} applogs have an invalid format, e.g. ${stringify(typeboxErrors[0])}`,
		)
	}

	let encryptedForCurrentAgent = false
	let aesKey, publisherAg
	if (encryptedLogs.length) {
		;({ aesKey, publisherAg } = await getAESkeyForEncryptedApplogs(infoLogs) as any)
		encryptedForCurrentAgent = !!aesKey
	}
	DEBUG('[decrypt] isEncrypted?:', !!encryptedLogs.length, { aesKey, applogs /* , subToUpdate */ })

	const maybeDecryptedApplogs = [] as Applog[]
	let hasDecryptionErrors = false
	let isRetry = false

	async function tryIntegrate() {
		for (const eachLog of encryptedLogs) {
			if (!aesKey) {
				hasDecryptionErrors || ERROR('encrypted log(s) found but no pubSub Key found', { aesKey, applogs /* , subToUpdate */ })
				hasDecryptionErrors = true
				continue
			}
			VERBOSE('[decrypt] attempting decryption:', eachLog)
			const decrypted: Applog = await decryptWithAesSharedKey(eachLog.enc, aesKey, 'parsed')
			const pv = decrypted.pv ? decrypted.pv.toString() : null
			VERBOSE('[decrypt] decrypted:', { decrypted })
			maybeDecryptedApplogs.push({ ...decrypted, pv, cid: encodeApplogAndGetCid(decrypted).toString() }) // locally we want pv as a string
		}
		for (const eachLog of nonEncryptedLogs) {
			if (isValidApplog(eachLog)) {
				const pv = eachLog.pv ? eachLog.pv.toString() : null
				maybeDecryptedApplogs.push({ ...eachLog, pv }) // locally we want pv as a string
			} else {
				const errors = getApplogTypeErrors(eachLog)
				WARN('[pull] unknown log problem', eachLog, unwrapIfSingle(errors))
			}
		}

		const newApplogs = maybeDecryptedApplogs.filter((newLog: any) => !thread.hasApplog(newLog as Applog, false))
		const deduplicated = removeDuplicateAppLogs(newApplogs, 'safety')
		if (deduplicated.length != newApplogs.length) {
			WARN(`Removed ${newApplogs.length - deduplicated.length} duplicate applogs`, { newApplogs, deduplicated })
		}
		DEBUG(`[tryIntegrate->${thread.nameAndSizeUntracked}] New Applogs:`, deduplicated.length, {
			deduplicated,
			maybeDecryptedApplogs,
			thread,
		})
		if (deduplicated.length) {
			integratePub({ targetThread: thread, agentHash: agent.ag, subID, pubData: { cid, thread: threadFromMaybeArray(deduplicated) } })
		}
	}
	await tryIntegrate() // first try

	if (hasDecryptionErrors) {
		WARN('trying to solve encryption errore', { aesKey, nonEncryptedLogs, applogs /* , subToUpdate , ipnsNameString */ })
		if (isRetry) {
			ERROR('second round of errors')
		} else {
			isRetry = true
			let derivationJWKstring
			const isForMe = false
			// try to infer encryption and setup subscription before retrying
			if (!encryptedForCurrentAgent) {
				notifyToast(
					`Found Encrypted Applogs that are not shared with us (author: ${getAgentString(publisherAg)} [${publisherAg}])).`,
					'warning',
					Number.POSITIVE_INFINITY,
				)
			}
			DEBUG('[decrypt] Errors found retrying now', { isForMe, publisherAg, derivationJWKstring /* , subToUpdate */ })

			if (isForMe && derivationJWKstring) await tryIntegrate() // ? only if its for me and i have the remote jwk, i think ?
		}
	}

	// Co-publishing key recovery: scan the decrypted applogs for `share/copubkey`
	// records. The originator wrote one — encrypted with the share content key, so
	// it only reaches `maybeDecryptedApplogs` once decryption succeeded — carrying
	// the IPNS private key encrypted per-co-publisher. If this agent is in the
	// record's `encKeyMap`, recover `pk`, verify it reconstructs to the share's
	// IPNS name, and register the share locally as co-publishable so this agent
	// can push to the same name. Errors here must not break the pull integration.
	await tryRecoverCoPublishKeys(maybeDecryptedApplogs, agent, subID)

	DEBUG(`[tryIntegrate->${thread.nameAndSizeUntracked}] Successfully integrated`)
	return { hasDecryptionErrors, encryptedLogs, encryptedForCurrentAgent }
}

/**
 * Scan decrypted applogs for `share/copubkey` records and, when the pulling
 * agent is one of the co-publishers (its `ag` is in the record's `encKeyMap`),
 * recover the IPNS private key and register the share locally as co-publishable.
 *
 * The record rides the encrypted-applog path, so it only appears here after the
 * share content key successfully decrypted it. Agents not listed in `encKeyMap`
 * are ignored (they keep seeing the share as an ordinary subscriber). Any
 * recovery/registration error is caught and logged so it can never break the
 * surrounding pull integration.
 */
async function tryRecoverCoPublishKeys(applogs: Applog[], agent: AgentStateClass, subID: string) {
	const myAg = agent.ag
	const coPubKeyLogs = applogs.filter(log => log.at === 'share/copubkey')
	if (!coPubKeyLogs.length) return

	for (const log of coPubKeyLogs) {
		const originatorAg = log.ag
		// `en` is the reserved `share:<id>` entity; the id is the IPNS name.
		const en = log.en
		const shareId = typeof en === 'string' && en.startsWith('share:') ? en.slice('share:'.length) : subID
		if (!shareId) {
			WARN('[co-publish] share/copubkey log without a resolvable share id; skipping', { log, subID })
			continue
		}

		const rawVl = log.vl
		let record: CoPubKeyRecord
		try {
			record = (typeof rawVl === 'string' ? JSON.parse(rawVl) : rawVl) as CoPubKeyRecord
		} catch (err) {
			WARN('[co-publish] failed to parse share/copubkey vl; skipping', { err, rawVl, shareId })
			continue
		}

		// Authoritative co-publisher check on the pull side: this agent must have
		// an ECDH-encrypted AES entry. If not, ignore the key record — the agent is
		// an ordinary subscriber and must never see the publish key.
		if (!record.encKeyMap?.[myAg]) {
			DEBUG('[co-publish] not a co-publisher for this share; ignoring key record', { shareId, myAg, originatorAg })
			continue
		}

		try {
			const pk = await recoverCoPublishKey(record, agent, originatorAg)
			// Verify the recovered key reconstructs to the share's IPNS name.
			const name = await W3Name.from(pk)
			if (name.toString() !== shareId) {
				ERROR('[co-publish] recovered key does not match share id; NOT registering', { recovered: name.toString(), shareId, originatorAg })
				continue
			}

			const coPublishers = Object.keys(record.encKeyMap)
			const existing = agent.sharesMap.get(shareId)
			if (existing) {
				// Idempotent: skip if already registered as co-publishable by the same originator.
				if (existing.coPublish && existing.originatorAg === originatorAg && isCoPublisher(existing, myAg)) {
					DEBUG('[co-publish] share already registered as co-publishable; skipping', { shareId, originatorAg })
					continue
				}
				await agent.updateShare(shareId, {
					pk,
					coPublish: true,
					originatorAg,
					coPublishers,
				})
				LOG('[co-publish] registered existing share as co-publishable', { shareId, originatorAg, coPublishers })
			} else {
				// The co-publisher only had a subscription — create the local share
				// record (with the recovered pk) so they can push to the same IPNS
				// name. Carry over encryption metadata from the subscription so content
				// encryption stays consistent with the originator's setup.
				const sub = agent.subscriptionsMap.get(shareId)
				const newShare: IShare = {
					id: shareId,
					createdAt: dateNowIso(),
					name: sub?.name ?? `co:${shareId.slice(-7)}`,
					pk,
					autopush: false,
					lastPush: null,
					publishedBy: myAg,
					coPublish: true,
					originatorAg,
					coPublishers,
					encryptedFor: sub?.encryptedFor ?? null,
					encryptedWith: sub?.encryptedWith ?? null,
				}
				await agent.addShare(newShare)
				LOG('[co-publish] created local co-publish share from subscription', { shareId, originatorAg, coPublishers })
			}
		} catch (err) {
			ERROR('[co-publish] failed to recover/register co-publish key', { shareId, originatorAg, err })
		}
	}
}

export async function retrievePubAsThread(appThread: Thread, pubID: CidString, pinCID?: string) {
	const writableThread = ThreadInMemory.empty(`pub-${pubID}`)
	const { cid: parsedPubID, errors, isIpns } = tryParseCID(pubID)
	if (!parsedPubID) throw ERROR(`Failed to parse pubID`, pubID, errors)
	const { cid, applogsCID, applogs: maybeEncryptedApplogs, info, counterInfo } = await retrievePubToThread(
		writableThread,
		isIpns ? toIpnsString(parsedPubID) : null,
		{ pinCID: isIpns ? pinCID : cidToString(parsedPubID) },
		appThread,
	)
	VERBOSE('retrievePubToThread returned:', { cid, applogsCID, maybeEncryptedApplogs, info, counterInfo })
	insertCompleteApplogsInAppDBifMissing(info.logs)
	const thread = ThreadInMemory.fromArray([...writableThread.applogs], `preview-${pubID}`, true) // HACK: bc. readonly - better would be a readOnly(thread) wrapper
	return { thread, cid, applogsCID, maybeEncryptedApplogs, info, counterInfo }
}

/**
 * Create a preview resource that supports incremental updates via WebSocket.
 * Returns a writable thread and an update function that fetches only new applogs.
 */
export function createPreviewState(appThread: Thread, pubID: CidString) {
	const { cid: parsedPubID, errors, isIpns } = tryParseCID(pubID)
	if (!parsedPubID) throw ERROR(`Failed to parse pubID`, pubID, errors)

	const writableThread = ThreadInMemory.empty(`preview-${pubID}`)
	const readOnlyView = asReadOnly(writableThread)
	let currentCID: CID | null = null
	let currentInfo: { logs: Applog[] } | null = null
	type PreviewGuard = { isActive?: () => boolean }

	return {
		thread: readOnlyView,
		get cid() {
			return currentCID
		},
		get info() {
			return currentInfo
		},

		/** Initial load - fetches all applogs */
		async load(pinCID?: string, guard: PreviewGuard = {}) {
			const result = await retrievePubToThread(
				writableThread,
				isIpns ? toIpnsString(parsedPubID) : null,
				{ pinCID: isIpns ? pinCID : cidToString(parsedPubID), progressKey: pubID },
				appThread,
			)
			if (guard.isActive && !guard.isActive()) {
				DEBUG('[createPreviewState] Initial load ignored (inactive)', { pubID, cid: result.cid?.toString() })
				return result
			}
			currentCID = result.cid
			currentInfo = result.info
			insertCompleteApplogsInAppDBifMissing(result.info.logs)
			LOG('[createPreviewState] Initial load complete:', { cid: currentCID?.toString(), applogs: writableThread.applogs.length })
			return result
		},

		/** Incremental update - fetches only new applogs since current CID */
		async update(newCID?: string, guard: PreviewGuard = {}) {
			if (!currentCID) throw ERROR('[createPreviewState] Cannot update before initial load')

			const lastCID = currentCID.toString()
			const result = await retrievePubToThread(
				writableThread,
				isIpns ? toIpnsString(parsedPubID) : null,
				{ pinCID: newCID, lastCID, progressKey: pubID },
				appThread,
			)
			if (guard.isActive && !guard.isActive()) {
				DEBUG('[createPreviewState] Incremental update ignored (inactive)', { pubID, lastCID })
				return result
			}

			if (result.didUpdate) {
				currentCID = result.cid
				currentInfo = result.info
				insertCompleteApplogsInAppDBifMissing(result.info.logs)
				LOG('[createPreviewState] Incremental update complete:', {
					previousCID: lastCID,
					newCID: currentCID?.toString(),
					totalApplogs: writableThread.applogs.length,
				})
			} else {
				LOG('[createPreviewState] No new updates')
			}
			return result
		},

		/** Get extra info for the preview (overlap, etc) */
		getExtras() {
			return {
				id: pubID,
				thread: readOnlyView,
				cid: currentCID,
				info: currentInfo,
				...getPubExtraInfo(appThread, writableThread),
			}
		},
	}
}
export async function retrievePubDataWithExtras(appThread: Thread, pubID: CidString, pinCID?: string) {
	const stuff = await retrievePubAsThread(appThread, pubID, pinCID)
	return {
		id: pubID,
		...stuff,
		...getPubExtraInfo(appThread, stuff.thread),
		encryptedCount: stuff.maybeEncryptedApplogs.filter(isEncryptedApplog).length, // HACK refactor
	}
}
export async function decodePubCarWithExtras(appThread: Thread, car: CarReader) {
	const { cid, applogs: applogsMaybeEnc, info } = await decodePubFromCar(car)
	const [encryptedLogs, applogs] = partition(applogsMaybeEnc, isEncryptedApplog)
	const thread = ThreadInMemory.fromArray(applogs, `preview-${cid.toString()}`, true) // HACK: bc. readonly - better would be a readOnly(thread) wrapper
	return {
		id: cid.toString(),
		cid,
		info,
		applogs,
		thread,
		...getPubExtraInfo(appThread, thread),
		encryptedCount: encryptedLogs.length,
	}
}
export function getPubExtraInfo(appThread: Thread, thread: Thread) {
	// const entityOverlappingVMmap = entityOverlapMap(appThread, thread)  // PERF: computed but never used
	// DEBUG({ entityOverlappingVMmap })
	return {
		entityOverlapCount: entityOverlapCount(appThread, thread),
		agents: Array.from(agentsOfThread(thread).keys()),
		newLogs: thread.applogs.filter(log => !appThread.hasApplog(log, false)).length,
	}
}
